import React, { useState, ReactNode, useMemo, useEffect, lazy, Suspense } from 'react';
import { Outlet, useNavigate, useLocation, useMatch, Link } from 'react-router-dom';

// Lazy load the API config banner to reduce bundle size
const ApiConfigBanner = lazy(() => import('../common/ApiConfigBanner'));
import { ThemeProvider } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import menuApi, { type MenuItem as ApiMenuItem } from '../../api/menuApi';
import { styled, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { 
  Box, Drawer as MuiDrawer, AppBar as MuiAppBar, 
  type AppBarProps as MuiAppBarProps, Toolbar, List, Typography, 
  Divider, IconButton, ListItem, ListItemButton, ListItemIcon, 
  CssBaseline, Tooltip, Avatar, Badge, Menu, MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon, 
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon, 
  People as PeopleIcon, 
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon, 
  Person as PersonIcon, 
  Logout as LogoutIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Business as BusinessIcon, 
  Settings as SettingsIcon, 
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon, 
  Payment as PaymentIcon,
  Group as GroupIcon, 
  PersonAdd as PersonAddIcon,
  Straighten as StraightenIcon, 
  BarChart as BarChartIcon, 
  VpnKey as VpnKeyIcon,
  LocalHospital as LocalHospitalIcon,
  LocationOn as LocationOnIcon,
  ManageAccounts as ManageAccountsIcon,
  Badge as BadgeIcon,
  GroupWork as GroupWorkIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingCart as ShoppingCartIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  Input as InputIcon,
  Output as OutputIcon,
  SyncAlt as SyncAltIcon,
  ListAlt as ListAltIcon,
  AssessmentOutlined as AssessmentOutlinedIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Speed as SpeedIcon,
  LocalPharmacy as LocalPharmacyIcon
} from '@mui/icons-material';
import ThemeToggle from '../theme/ThemeToggle';
import type { UserRole } from '../../types/userRole';

// Navigation item type
interface NavItem {
  id: string;
  text: string;
  icon: ReactNode;
  path: string;
  roles?: (string | UserRole)[];
  allowedRoles?: (string | UserRole)[];
  children?: NavItem[];
  isGroup?: boolean;
  order?: number;
}

interface MainLayoutProps {
  children?: ReactNode;
}

// Styled components
const drawerWidth = 260;

interface MainProps {
  open?: boolean;
  theme?: Theme;
}

// Helper function to type check property forwarding
const shouldForwardProp = (prop: string): boolean => prop !== 'open';

interface MainComponentProps extends React.HTMLAttributes<HTMLElement> {
  open?: boolean;
}

const Main = styled('main', { 
  shouldForwardProp: (prop: string) => shouldForwardProp(prop)
})<MainComponentProps>(({ theme, open }) => ({ // eslint-disable-line @typescript-eslint/no-unused-vars
  flexGrow: 1,
  padding: theme.spacing(2),
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  width: '100%',
  ...(open && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: `calc(100% - ${drawerWidth}px)`,
  }),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5),
    marginLeft: '0 !important',
    width: '100% !important',
  },
}));

interface AppBarComponentProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop: string) => shouldForwardProp(prop)
})<AppBarComponentProps>(({ theme, open }) => ({ // eslint-disable-line @typescript-eslint/no-unused-vars
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginLeft: 0,
  },
}));

interface DrawerProps {
  open?: boolean;
}

const collapsedWidth = 72; // Width when drawer is collapsed

const Drawer = styled(MuiDrawer, { 
  shouldForwardProp: (prop: string) => shouldForwardProp(prop)
})<DrawerProps>(
  ({ theme, open }) => ({
    width: open ? drawerWidth : collapsedWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    height: '100vh',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '& .MuiDrawer-paper': {
      position: 'fixed',
      top: 0,
      left: 0,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      width: open ? drawerWidth : collapsedWidth,
      height: '100%',
      overflowX: 'hidden',
      overflowY: 'auto',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      [theme.breakpoints.down('md')]: {
        position: 'fixed',
        height: '100%',
      },
    },
  })
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(true);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [menuItems, setMenuItems] = useState<NavItem[]>([]);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    '/patient-management': true,
    '/doctor': true,
    '/settings': true,
    '/inventory': true,
    '/reports': true,
  });
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const match = useMatch({ path: location.pathname });

  // Map of icon names to actual icon components
  const iconMap: Record<string, ReactNode> = useMemo(() => ({
    dashboard: <DashboardIcon />,
    people: <PeopleIcon />,
    event: <CalendarTodayIcon />,
    inventory: <InventoryIcon />,
    inventory2: <Inventory2Icon />,
    settings: <SettingsIcon />,
    business: <BusinessIcon />,
    local_hospital: <LocalHospitalIcon />,
    receipt: <ReceiptIcon />,
    assessment: <AssessmentIcon />,
    payment: <PaymentIcon />,
    group: <GroupIcon />,
    manage_accounts: <ManageAccountsIcon />,
    location_on: <LocationOnIcon />,
    menu: <MenuIcon />,
    list_alt: <ListAltIcon />,
    add: <AddIcon />,
    remove: <RemoveIcon />,
    // Add more icons as needed
  }), []);

  // Use default menu items
  useEffect(() => {
    setMenuItems(defaultMenuItems);
  }, []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setActiveSubmenu(null);
  };

  const handleSubmenuToggle = (text: string) => {
    setActiveSubmenu(activeSubmenu === text ? null : text);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  // Default menu items
  const defaultMenuItems: NavItem[] = [
    {
      id: 'dashboard',
      text: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      isGroup: false,
      order: 1
    },
    {
      id: 'patient-management',
      text: 'Patient Management',
      path: '/patient-management',
      icon: <PeopleIcon />,
      isGroup: true,
      order: 2,
      children: [
        {
          id: 'patients',
          text: 'Patients',
          path: '/patient-management/patients',
          icon: <PeopleIcon />,
          isGroup: false,
          order: 1
        },
        {
          id: 'appointments',
          text: 'Appointments',
          path: '/patient-management/appointments',
          icon: <CalendarTodayIcon />,
          isGroup: false,
          order: 2
        },       
      ]
    },
    {
      id: 'Inventory',
      text: 'Inventory',
      path: '/inventory',
      icon: <InventoryIcon />,
      isGroup: true,
      order: 4,
      children: [
        {
          id: 'items',
          text: 'Items',
          path: '/settings/items',
          icon: <Inventory2Icon />,
          isGroup: false,
          order: 1
        },
        {
          id: 'transactions',
          text: 'Transactions',
          path: '/inventory/transactions',
          icon: <ReceiptIcon />,
          isGroup: false,
          order: 2
        },
        {
          id: 'reports',
          text: 'Reports',
          path: '/inventory/report',
          icon: <OutputIcon />,
          isGroup: false,
          order: 6,
          children : [
            {
              id: 'item-ledger',
              text: 'Item Ledger',
              path: '/inventory/reports/item-ledger',
              icon: <AssessmentOutlinedIcon />,
              isGroup: false,
              order: 1
            },
            {
              id: 'stock-balance',
              text: 'Stock Balance',
              path: '/inventory/reports/stock-balance',
              icon: <BarChartIcon />,
              isGroup: false,
              order: 2
            },
            {
              id: 'fsn-analysis',
              text: 'FSN Analysis',
              path: '/inventory/reports/fsn-analysis',
              icon: <SpeedIcon />,
              isGroup: false,
              order: 3
            }
          ]
        },
      ]
    },
    {
      id: 'settings',
      text: 'Settings',
      path: '/settings',
      icon: <SettingsIcon />,
      isGroup: true,
      order: 6,
      children: [
        { 
          id: 'company',
          text: 'Company Profile',
          path: '/settings/company',
          icon: <BusinessIcon />,
          isGroup: false,
          order: 1
        },
        { 
          id: 'departments',
          text: 'Departments',
          path: '/settings/departments',
          icon: <BusinessIcon />,
          isGroup: false,
          order: 2
        },
        { 
          id: 'locations',
          text: 'Locations',
          path: '/settings/locations',
          icon: <LocationOnIcon />,
          isGroup: false,
          order: 3
        },
        {
          id: 'Facilities',
          text: 'Facilities',
          path: '/facilities',
          icon: <MenuIcon />,
          isGroup: false,
          order: 5
        },
        {
          id: 'users',
          text: 'Users',
          path: '/settings/users',
          icon: <MenuIcon />,
          isGroup: false,
          order: 6
        }
      ]
    },
    {
      id: 'accounts',
      text: 'Accounts',
      path: '/accounts',
      icon: <PaymentIcon />,
      isGroup: true,
      order: 5,
      children: [
        {
          id: 'chart-of-accounts',
          text: 'Chart of Accounts',
          path: '/accounts/chart-of-accounts',
          icon: <ListAltIcon />,
          isGroup: false,
          order: 1
        },
        {
          id: 'transactions',
          text: 'Transactions',
          path: '/accounts/transactions',
          icon: <RemoveIcon />,
          isGroup: false,
          order: 2
        }
      ]
    },
    {
      id: 'Reports',
      text: 'Reports',
      path: '/reports',
      icon: <AssessmentIcon />,
      isGroup: true,
      order: 6,
      children: [
        {
          id: 'financial-report',
          text: 'Financial Report',
          path: '/reports/financial',
          icon: <PaymentIcon />,
          isGroup: false,
          order: 1
        },
        {
          id: 'collection-report',
          text: 'Collection Report',
          path: '/reports/collection',
          icon: <PaymentIcon />,
          isGroup: false,
          order: 2
        },
        {
          id: 'service-report',
          text: 'Service Report',
          path: '/reports/services',
          icon: <LocalHospitalIcon />,
          isGroup: false,
          order: 3
        },
        {
          id: 'appointment-report',
          text: 'Appointment Report',
          path: '/reports/appointments',
          icon: <CalendarTodayIcon />,
          isGroup: false,
          order: 4
        },
        {
          id: 'pending-payment-report',
          text: 'Pending Payment Report',
          path: '/reports/pending-payments',
          icon: <PaymentIcon />,
          isGroup: false,
          order: 5
        },
        {
          id: 'pending-dispensing-report',
          text: 'Pending Dispensing Report',
          path: '/reports/pending-dispensing',
          icon: <LocalPharmacyIcon />,
          isGroup: false,
          order: 6
        },
        {
          id: 'patient-statement',
          text: 'Patient Statement',
          path: '/reports/patient-statement',
          icon: <ReceiptIcon />,
          isGroup: false,
          order: 7
        },
        {
          id: 'transaction-report',
          text: 'Transaction Report',
          path: '/reports/transactions',
          icon: <ReceiptIcon />,
          isGroup: false,
          order: 8
        }
      ]
    }
  ];

  // Use menuItems if available, otherwise fall back to defaultMenuItems
  const navItems = menuItems.length > 0 ? menuItems : defaultMenuItems;

  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const renderNavItems = (items: NavItem[] = [], isSubmenu = false) => {
    return items.map((item) => {
      // All menu items are now visible to all users
      // Role-based access control will be handled at the page/route level
      
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedMenus[item.path] ?? false;
      const isActive = location.pathname === item.path || 
                     (hasChildren && (item.children?.some(child => location.pathname.startsWith(child.path)) ?? false));
      
      return (
        <React.Fragment key={item.path}>
          <ListItem 
            disablePadding 
            sx={{ 
              display: 'block',
              bgcolor: isActive ? 'action.selected' : 'transparent',
              '&:hover': {
                bgcolor: hasChildren ? 'transparent' : 'action.hover',
              },
            }}
          >
            <Tooltip title={!open ? item.text : ''} placement="right">
              <ListItemButton
                component={hasChildren ? 'div' : Link}
                to={item.path || '#'}
                onClick={(e: { preventDefault: () => void; stopPropagation: () => void; }) => {
                  if (hasChildren) {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleMenu(item.path);
                  } else {
                    // Close the drawer on mobile after navigation
                    if (window.innerWidth < 900) {
                      setOpen(false);
                      setActiveSubmenu(null);
                    }
                    // Force a navigation to ensure the route changes
                    navigate(item.path || '/');
                  }
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    textDecoration: 'none',
                    backgroundColor: 'action.hover'
                  },
                  ...(isActive && {
                    backgroundColor: 'action.selected',
                    '&:hover': {
                      backgroundColor: 'action.selected'
                    }
                  })
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: isActive ? 'primary.main' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <Box 
                  sx={{ 
                    opacity: open ? 1 : 0,
                    width: open ? 'auto' : 0,
                    transition: 'opacity 0.2s, width 0.2s',
                    overflow: 'hidden',
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ 
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {item.text}
                  </Typography>
                  {hasChildren && open && (
                    <ExpandMoreIcon 
                      sx={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        ml: 1,
                      }}
                    />
                  )}
                </Box>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          {hasChildren && open && isExpanded && item.children && (
            <Box sx={{ pl: 2 }}>
              {renderNavItems(item.children, true)}
            </Box>
          )}
        </React.Fragment>
      );
    });
  };

  // Close drawer on mobile when route changes
  React.useEffect(() => {
    if (window.innerWidth < 900) {
      setOpen(false);
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        open={open}
        sx={{
          width: { md: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: open ? `${drawerWidth}px` : 0 },
          transition: (theme: Theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 2,
            }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.firstName ? `${user.firstName}'s Dashboard` : 'HMS Dashboard'}
          </Typography>
          
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* User Profile */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          navigate('/profile');
          if (window.innerWidth < 600) {
            setOpen(false);
            setActiveSubmenu(null);
          }
        }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => {
          navigate('/settings');
          if (window.innerWidth < 600) {
            setOpen(false);
            setActiveSubmenu(null);
          }
        }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      
      <Box
        component="nav"
        sx={(theme) => ({
          width: { sm: open ? drawerWidth : 0 },
          flexShrink: { sm: 0 },
          [theme.breakpoints.up('md')]: {
            width: open ? drawerWidth : theme.spacing(9),
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        })}
        aria-label="mailbox folders"
      >
        <Drawer
          variant={window.innerWidth < 900 ? 'temporary' : 'permanent'}
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box',
              width: open ? drawerWidth : collapsedWidth,
              [theme.breakpoints.down('md')]: {
                width: open ? drawerWidth : 0, // Still collapse to 0 on mobile
              },
            },
          }}
        >
          <DrawerHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 1 }}>
                        <LocalHospitalIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
                        {open && (
                          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
                            Scare Pro
                          </Typography>
                        )}
                      </Box>
          </DrawerHeader>
          <Divider />
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 64px)', // Adjust based on your header height
            overflow: 'hidden',
          }}>
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {renderNavItems(navItems)}
            </Box>
            <Divider />
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ width: 40, height: 40, mr: 1, bgcolor: 'primary.main' }}>
                  {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0, opacity: open ? 1 : 0, transition: 'opacity 0.2s' }}>
                  <Typography variant="subtitle2" noWrap>
                    {user?.firstName && user?.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user?.username || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{user?.email || ''}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Drawer>
      </Box>
      
      <Main 
        open={open}
        sx={{
          mt: { xs: '56px', sm: '64px' }, // Height of AppBar
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          position: 'relative',
          minHeight: 'calc(100vh - 56px)', // Full height minus AppBar height
          '@media (min-width: 600px)': {
            minHeight: 'calc(100vh - 64px)', // Adjust for larger screens
          },
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%', pb: 8 }}>
          {children || <Outlet />}
          
          {/* API Configuration Banner */}
          <Suspense fallback={null}>
            <ApiConfigBanner />
          </Suspense>
        </Box>
      </Main>
    </Box>
  );
};

export default MainLayout;
